//
// Vitals entry — right-side drawer with grouped sections, range validation and normal-range
// suggestions. Data-driven from the server's vital_definitions form; grouping/reference data
// below only decorate whatever fields the form returns (unknown fields land in their API
// section). Used everywhere vitals are recorded (calendar/schedule/appointments strips,
// mentor vitals listing, kiosk manage-bookings) via the same export + props as before.
//
import { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material'
import { toast } from 'react-toastify'
import CloseIcon from '@mui/icons-material/Close'
import DoneIcon from '@mui/icons-material/Done'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined'
import { postCPDataWithParams } from 'src/clinic/services'
import { getPatientsByIds } from 'src/emr/database/indexedDbDao'
import { loadOptionsForExistingPatient } from 'src/clinic/components/ClinicManageBookingsPage/patientForm/services/patientSearchService'
import { useVitalsForm } from 'src/reducers/vitalsFormSettings/hooks/useVitalsForm'
import {
  convertFromBaseUnit,
  validateField,
} from 'src/reducers/vitalsFormSettings/utils/vitalsUtils'

// ── Curated clinical grouping (by canonical field id). Fields the form returns that are not
//    listed here fall back to their API `section` (title-cased) so nothing ever disappears. ────
const FIELD_GROUPS = [
  {
    id: 'core',
    title: 'Core Vitals',
    fields: [
      'systolicBpInMmHg',
      'diastolicBpInMmHg',
      'bpm',
      'tempInCelsius',
      'respiratoryRatePerMin',
      'sp02',
      'rbsInMgPerDl',
    ],
  },
  {
    id: 'body',
    title: 'Height & Weight',
    fields: [
      'heightInCms',
      'weightInGrams',
      'bmi',
      'obesityLevel',
      'idealBodyWeightInGrams',
      'bodyAge',
      'bodyType',
    ],
  },
  {
    id: 'girth',
    title: 'Girth Measurements',
    fields: [
      'waistCircumferenceInCm',
      'hipCircumferenceInCm',
      'waistHipRatio',
      'abdominalGirthInCm',
    ],
    collapsible: true,
  },
  {
    id: 'obgyn',
    title: 'OB / GYN',
    fields: [
      'lastMenstrualPeriod',
      'expectedDateOfDelivery',
      'gestationAgeInDays',
      'completedCycles',
    ],
    collapsible: true,
  },
  {
    id: 'composition',
    title: 'Body Composition',
    fields: [
      'bodyFatPercentage',
      'visceralFat',
      'subcutaneousFatPercentage',
      'fatMassInGrams',
      'fatFreeBodyWeightInGrams',
      'muscleMassInGrams',
      'muscleRatePercentage',
      'skeletalMusclePercentage',
      'proteinPercentage',
      'proteinMassInGrams',
      'waterWeightInGrams',
      'bodyWaterPercentage',
      'boneMassInGrams',
      'bmrInKcal',
      'cardiacIndex',
    ],
    collapsible: true,
  },
  { id: 'notes', title: 'Notes', fields: ['guidance'] },
]

// ── Reference ranges in each field's BASE unit. `hard` bounds block saving (implausible entry);
//    `normal` bounds only decorate the value with a Low / High suggestion chip. ────────────────
const VITAL_REFERENCE = {
  systolicBpInMmHg: { hardMin: 40, hardMax: 300, normal: [90, 129] },
  diastolicBpInMmHg: { hardMin: 20, hardMax: 200, normal: [60, 84] },
  bpm: { hardMin: 20, hardMax: 250, normal: [60, 100] },
  sp02: { hardMin: 40, hardMax: 100, normal: [95, 100] },
  tempInCelsius: { hardMin: 30, hardMax: 45, normal: [36.1, 37.5] },
  respiratoryRatePerMin: { hardMin: 4, hardMax: 80, normal: [12, 20] },
  rbsInMgPerDl: { hardMin: 20, hardMax: 700, normal: [70, 140] },
  heightInCms: { hardMin: 20, hardMax: 272 },
  weightInGrams: { hardMin: 500, hardMax: 500000 },
  bmi: { normal: [18.5, 24.9] },
  waistCircumferenceInCm: { hardMin: 20, hardMax: 250 },
  hipCircumferenceInCm: { hardMin: 20, hardMax: 250 },
  abdominalGirthInCm: { hardMin: 20, hardMax: 250 },
  bodyFatPercentage: { hardMin: 1, hardMax: 75 },
  visceralFat: { hardMin: 1, hardMax: 60 },
  subcutaneousFatPercentage: { hardMin: 1, hardMax: 70 },
  muscleRatePercentage: { hardMin: 5, hardMax: 90 },
  skeletalMusclePercentage: { hardMin: 5, hardMax: 70 },
  proteinPercentage: { hardMin: 2, hardMax: 40 },
  bodyWaterPercentage: { hardMin: 20, hardMax: 85 },
  gestationAgeInDays: { hardMin: 0, hardMax: 320 },
  completedCycles: { hardMin: 0, hardMax: 30 },
  bodyAge: { hardMin: 1, hardMax: 120 },
}

const titleCase = (raw) =>
  String(raw || '')
    .replaceAll('_', ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase())

const round1 = (value) =>
  typeof value === 'number' && Number.isFinite(value) ? Math.round(value * 10) / 10 : value

/** Format a base-unit bound in the field's currently selected unit. */
const boundInUnit = (bound, field, unit) => round1(convertFromBaseUnit(bound, field, unit))

/**
 * Status of one field's BASE-unit value against the reference table (+ API ui.max when given):
 * 'invalid' blocks save, 'low'/'high' are advisory, null = nothing to say.
 */
function getFieldStatus(field, baseValue) {
  if (baseValue === null || baseValue === undefined || baseValue === '') return null
  if (typeof baseValue !== 'number' || !Number.isFinite(baseValue)) return null
  const ref = VITAL_REFERENCE[field.id] || {}
  const hardMax = ref.hardMax ?? field.ui?.max
  if ((ref.hardMin !== undefined && baseValue < ref.hardMin) || (hardMax !== undefined && baseValue > hardMax)) {
    return 'invalid'
  }
  if (ref.normal) {
    if (baseValue < ref.normal[0]) return 'low'
    if (baseValue > ref.normal[1]) return 'high'
    return 'normal'
  }
  return null
}

const STATUS_CHIP = {
  low: { label: 'Low', color: '#b45309', bg: 'rgba(245, 158, 11, 0.15)' },
  high: { label: 'High', color: '#b91c1c', bg: 'rgba(239, 68, 68, 0.14)' },
  normal: { label: 'OK', color: '#047857', bg: 'rgba(16, 185, 129, 0.14)' },
}

/** ISO-ish (or epoch) → value for <input type="datetime-local">, in local time. */
function toDatetimeLocalValue(raw) {
  if (!raw) return ''
  const date = new Date(raw)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (n) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function SectionHeader({ title, count, filled, collapsible, open, onToggle }) {
  // Non-collapsible sections keep a slim caption divider.
  if (!collapsible) {
    return (
      <Box sx={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
        <Box
          component="span"
          sx={{
            fontSize: '11px',
            fontWeight: 800,
            letterSpacing: '0.6px',
            textTransform: 'uppercase',
            color: '#4338ca',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </Box>
        {filled > 0 && (
          <Box
            component="span"
            sx={{
              px: '6px',
              borderRadius: '999px',
              backgroundColor: 'rgba(67, 56, 202, 0.1)',
              color: '#4338ca',
              fontSize: '10px',
              fontWeight: 700,
            }}
          >
            {filled}/{count}
          </Box>
        )}
        <Box sx={{ flex: 1, height: '1px', backgroundColor: 'rgba(100, 116, 139, 0.18)' }} />
      </Box>
    )
  }

  // Collapsible sections render as a big, unmistakably tappable full-width bar — clinic staff
  // entering vitals are often non-technical, so the whole row is one large button with a loud
  // open/close affordance instead of a thin label + tiny chevron.
  return (
    <Box
      component="button"
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      sx={{
        gridColumn: '1 / -1',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mt: 0.5,
        minHeight: 46,
        width: '100%',
        px: '12px',
        border: `1px solid ${open ? 'rgba(67, 56, 202, 0.32)' : 'rgba(67, 56, 202, 0.2)'}`,
        borderRadius: '10px',
        backgroundColor: open ? 'rgba(67, 56, 202, 0.09)' : 'rgba(67, 56, 202, 0.045)',
        cursor: 'pointer',
        userSelect: 'none',
        textAlign: 'left',
        transition: 'background-color 120ms ease, border-color 120ms ease',
        '&:hover': { backgroundColor: 'rgba(67, 56, 202, 0.12)' },
        '&:active': { transform: 'scale(0.995)' },
        '&:focus-visible': { outline: '2px solid #4338ca', outlineOffset: '2px' },
      }}
    >
      <Box
        component="span"
        sx={{
          fontSize: '12.5px',
          fontWeight: 800,
          letterSpacing: '0.5px',
          textTransform: 'uppercase',
          color: '#4338ca',
          whiteSpace: 'nowrap',
        }}
      >
        {title}
      </Box>
      {filled > 0 && (
        <Box
          component="span"
          sx={{
            px: '7px',
            py: '1px',
            borderRadius: '999px',
            backgroundColor: 'rgba(67, 56, 202, 0.14)',
            color: '#4338ca',
            fontSize: '11px',
            fontWeight: 800,
          }}
        >
          {filled}/{count}
        </Box>
      )}
      <Box component="span" sx={{ flex: 1 }} />
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '3px',
          px: '10px',
          py: '4px',
          borderRadius: '999px',
          backgroundColor: open ? 'rgba(67, 56, 202, 0.16)' : '#4338ca',
          color: open ? '#4338ca' : '#ffffff',
          fontSize: '11.5px',
          fontWeight: 800,
          whiteSpace: 'nowrap',
        }}
      >
        {open ? 'Hide' : 'Open'}
        <ExpandMoreIcon
          sx={{
            fontSize: 20,
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 150ms ease',
          }}
        />
      </Box>
    </Box>
  )
}

export const VitalsComponentEntryDialog = ({
  patientId,
  clinicId,
  bookedEventId,
  closeFunction,
  vitalsFormGetApiSignature,
  callbackFunctionAfterSave,
  existingVitalData,
  // Patient's gender when the caller knows it ('male' | 'female' | ...). OB/GYN vitals are
  // hidden for male patients; any other/unknown value keeps them available.
  patientGender = null,
  // Patient number (e.g. HRM00298) — lets the drawer look the patient up when the booking
  // payload carries no demographics at all. The brand id scopes that lookup (the patient-list
  // endpoints return nothing without it).
  patientNumber = null,
  clinicBrandId = null,
}) => {
  const [saving, setSaving] = useState(false)
  const [openSections, setOpenSections] = useState({})
  // Best-effort gender when the caller couldn't supply one (CA booking payloads omit patient
  // demographics): the locally cached patient record (IndexedDB, zero network). Unknown → keep
  // every section available.
  const [resolvedGender, setResolvedGender] = useState(null)
  useEffect(() => {
    if (patientGender || !patientId) return undefined
    let cancelled = false
    const resolve = async () => {
      const cached = await getPatientsByIds([patientId]).catch(() => null)
      const cachedGender = cached?.[0]?.gender
      if (cachedGender) {
        if (!cancelled) setResolvedGender(cachedGender)
        return
      }
      // Last resort: one portal-appropriate patient search by patient number.
      if (!patientNumber) return
      const pathname = typeof window !== 'undefined' ? window.location.pathname : ''
      const patientSearchApiSignature =
        pathname.startsWith('/ca-home') || pathname.startsWith('/kiosk') || pathname.startsWith('/ca/')
          ? '/api/secure/ca/patient/list'
          : pathname.startsWith('/mentor-home') || pathname.startsWith('/mentor/') || pathname.startsWith('/teacher/')
            ? '/api/secure/mentor/patient/list'
            : null
      if (!patientSearchApiSignature) return
      try {
        const result = await loadOptionsForExistingPatient(String(patientNumber), [], {
          page: 1,
          clinicId,
          clinicBrandId,
          patientSearchApiSignature,
        })
        const match = result?.options?.find((option) => option?.id === patientId)
        if (!cancelled && match?.gender) setResolvedGender(match.gender)
      } catch (error) {
        // Gender stays unknown — every section remains available.
      }
    }
    resolve()
    return () => {
      cancelled = true
    }
  }, [patientGender, patientId, patientNumber, clinicId, clinicBrandId])

  const {
    vitalsFields,
    vitalData,
    selectedUnits,
    hasData,
    handleInputChange,
    handleUnitChange,
    getDisplayValue,
    getDefaultUnit,
    buildVitalSavePayload,
    setVitalData,
  } = useVitalsForm({
    vitalsFormGetApiSignature: vitalsFormGetApiSignature,
    existingVitalData: existingVitalData ?? null,
    restorePersistedUnits: true,
  })

  // Assemble the render plan: curated groups first, then any leftover fields bucketed by their
  // API section — so a clinic-specific field always shows up even if we've never heard of it.
  const sections = useMemo(() => {
    const byId = new Map(vitalsFields.map((f) => [f.id, f]))
    const used = new Set()
    const plan = []
    FIELD_GROUPS.forEach((group) => {
      const fields = group.fields.map((id) => byId.get(id)).filter(Boolean)
      fields.forEach((f) => used.add(f.id))
      if (fields.length > 0) plan.push({ ...group, fields })
    })
    const leftovers = vitalsFields.filter((f) => !used.has(f.id))
    const leftoverSections = new Map()
    leftovers.forEach((f) => {
      const key = titleCase(f.section) || 'Other'
      if (!leftoverSections.has(key)) leftoverSections.set(key, [])
      leftoverSections.get(key).push(f)
    })
    // Keep Notes last: splice leftover sections in just before it when present.
    const notesIndex = plan.findIndex((g) => g.id === 'notes')
    const extra = Array.from(leftoverSections, ([title, fields]) => ({
      id: `section-${title}`,
      title,
      fields,
    }))
    if (notesIndex === -1) return [...plan, ...extra]
    return [...plan.slice(0, notesIndex), ...extra, ...plan.slice(notesIndex)]
  }, [vitalsFields])

  const fieldHasValue = (field) => {
    const v = vitalData[field.id]
    return v !== null && v !== undefined && v !== ''
  }

  // OB/GYN vitals are not offered for male patients (others / non-binary / unknown keep them).
  // If a male-marked record somehow already carries OB/GYN data, the section stays visible —
  // previously captured values are never hidden.
  const effectiveGender = patientGender || existingVitalData?.gender || resolvedGender
  const isMalePatient = ['male', 'm'].includes(String(effectiveGender || '').trim().toLowerCase())
  const visibleSections = sections.filter((group) => {
    if (group.id !== 'obgyn' || !isMalePatient) return true
    return group.fields.some(fieldHasValue)
  })

  // Collapsible sections start open only when they already hold data.
  const isSectionOpen = (group) => {
    if (!group.collapsible) return true
    if (group.id in openSections) return openSections[group.id]
    return group.fields.some(fieldHasValue)
  }

  const invalidFields = useMemo(
    () =>
      vitalsFields.filter(
        (field) => field.type !== 'calculated' && getFieldStatus(field, vitalData[field.id]) === 'invalid',
      ),
    [vitalsFields, vitalData],
  )

  const recordedCount = vitalsFields.filter(
    (f) => f.type !== 'calculated' && fieldHasValue(f),
  ).length

  const saveVital = async () => {
    if (!hasData) {
      closeFunction()
      return
    }

    // API-declared validators (e.g. BP pairRequired / cross-field rules)
    for (const field of vitalsFields) {
      const { isValid, message } = validateField(field, vitalData[field.id], vitalData)
      if (!isValid && message) {
        toast.error(message)
        return
      }
    }

    setSaving(true)
    const toSendData = { ...buildVitalSavePayload(), patientId, clinicId }
    if (bookedEventId) {
      toSendData.bookedEventId = bookedEventId
    }

    try {
      await postCPDataWithParams('/api/secure/patient/vitals/save', toSendData, {})
      await callbackFunctionAfterSave()
      closeFunction()
    } catch (error) {
      console.error('Failed to save vitals:', error)
    } finally {
      setSaving(false)
    }
  }

  // ── Field renderers ───────────────────────────────────────────────────────────────────────

  const unitAdornment = (field, selectedUnit, { alsoApplyTo = [] } = {}) => {
    if (field.units && field.units.length > 1 && field.type !== 'calculated') {
      return (
        <TextField
          select
          variant="standard"
          value={selectedUnit?.code || ''}
          onChange={(e) => {
            const newUnit = field.units.find((u) => u.code === e.target.value)
            handleUnitChange(field.id, newUnit)
            alsoApplyTo.forEach((otherId) => handleUnitChange(otherId, newUnit))
          }}
          onClick={(e) => e.stopPropagation()}
          slotProps={{ input: { disableUnderline: true } }}
          sx={{
            minWidth: 44,
            '& .MuiSelect-select': { fontSize: '12px', color: '#64748b', py: 0, pr: '18px !important' },
          }}
        >
          {field.units.map((unit) => (
            <MenuItem key={unit.code} value={unit.code}>
              {unit.label}
            </MenuItem>
          ))}
        </TextField>
      )
    }
    const label = selectedUnit?.label || field.baseUnit
    return label ? (
      <Box component="span" sx={{ fontSize: '12px', color: '#94a3b8', whiteSpace: 'nowrap' }}>
        {label}
      </Box>
    ) : null
  }

  /** helperText + colors for a numeric field, from its status + normal range in selected unit. */
  const fieldDecoration = (field, selectedUnit) => {
    const status = getFieldStatus(field, vitalData[field.id])
    const ref = VITAL_REFERENCE[field.id] || {}
    const normalHint = ref.normal
      ? `${boundInUnit(ref.normal[0], field, selectedUnit)}–${boundInUnit(ref.normal[1], field, selectedUnit)}`
      : null
    if (status === 'invalid') {
      const hardMax = ref.hardMax ?? field.ui?.max
      return {
        error: true,
        helperText: `Outside plausible range${
          ref.hardMin !== undefined && hardMax !== undefined
            ? ` (${boundInUnit(ref.hardMin, field, selectedUnit)}–${boundInUnit(hardMax, field, selectedUnit)} ${selectedUnit?.label || ''})`
            : ''
        }`,
        chip: null,
      }
    }
    return {
      error: false,
      helperText: null,
      placeholder: normalHint || undefined,
      chip: status && STATUS_CHIP[status] ? STATUS_CHIP[status] : null,
    }
  }

  const statusChipNode = (chip) =>
    chip ? (
      <Box
        component="span"
        sx={{
          px: '5px',
          borderRadius: '6px',
          backgroundColor: chip.bg,
          color: chip.color,
          fontSize: '10px',
          fontWeight: 800,
          whiteSpace: 'nowrap',
        }}
      >
        {chip.label}
      </Box>
    ) : null

  const renderNumberField = (field) => {
    const isDisabled = saving || field.ui?.readOnly || field.type === 'calculated'
    const selectedUnit = selectedUnits[field.id] || getDefaultUnit(field)
    const displayValue = getDisplayValue(field)
    const deco = fieldDecoration(field, selectedUnit)

    return (
      <TextField
        key={field.id}
        size="small"
        type="number"
        label={
          field.type === 'calculated' ? (
            <span>
              {field.label}{' '}
              <span style={{ color: '#059669', fontSize: '0.85em' }}>· auto</span>
            </span>
          ) : (
            field.label
          )
        }
        value={displayValue ?? ''}
        disabled={isDisabled}
        error={deco.error}
        helperText={deco.helperText}
        placeholder={deco.placeholder}
        onChange={(e) => handleInputChange(field.id, e.target.value, selectedUnit)}
        slotProps={{
          inputLabel: { shrink: true },
          input: {
            endAdornment: (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                {statusChipNode(deco.chip)}
                {unitAdornment(field, selectedUnit)}
              </Box>
            ),
          },
        }}
        sx={{ '& .MuiInputBase-root': { backgroundColor: isDisabled ? 'rgba(241,245,249,0.6)' : '#fff' } }}
      />
    )
  }

  const renderDatetimeField = (field) => (
    <TextField
      key={field.id}
      size="small"
      type="datetime-local"
      label={field.label}
      value={toDatetimeLocalValue(vitalData[field.id])}
      disabled={saving || field.ui?.readOnly}
      onChange={(e) => {
        const raw = e.target.value
        setVitalData((prev) => ({
          ...prev,
          // Store as ISO-8601 (UTC) so the server's Instant fields parse it directly.
          [field.id]: raw ? new Date(raw).toISOString() : null,
        }))
      }}
      slotProps={{ inputLabel: { shrink: true } }}
    />
  )

  const renderTextareaField = (field) => (
    <TextField
      key={field.id}
      multiline
      minRows={2}
      maxRows={5}
      size="small"
      label={field.label}
      value={getDisplayValue(field) || ''}
      disabled={saving || field.ui?.readOnly}
      placeholder={`Enter ${String(field.label || '').toLowerCase()}…`}
      onChange={(e) => setVitalData((prev) => ({ ...prev, [field.id]: e.target.value }))}
      slotProps={{ inputLabel: { shrink: true } }}
      sx={{ gridColumn: '1 / -1' }}
    />
  )

  /** Blood pressure renders as one joint control (sys / dia + shared unit). */
  const renderBpPair = (sysField, diaField) => {
    const selectedUnit = selectedUnits[sysField.id] || getDefaultUnit(sysField)
    const sysDeco = fieldDecoration(sysField, selectedUnit)
    const diaDeco = fieldDecoration(diaField, selectedUnit)
    const chip = sysDeco.chip?.label === 'OK' ? diaDeco.chip || sysDeco.chip : sysDeco.chip || diaDeco.chip
    const error = sysDeco.error || diaDeco.error

    return (
      <Box key="bp-pair" sx={{ gridColumn: 'span 2', minWidth: 0 }}>
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            border: `1px solid ${error ? '#d32f2f' : 'rgba(0, 0, 0, 0.23)'}`,
            borderRadius: '4px',
            padding: '4px 10px 4px 12px',
            backgroundColor: '#fff',
            '&:focus-within': { borderColor: error ? '#d32f2f' : '#4338ca' },
          }}
        >
          <Box
            component="span"
            sx={{
              position: 'absolute',
              top: '-8px',
              left: '9px',
              px: '4px',
              backgroundColor: '#fff',
              fontSize: '11px',
              color: error ? '#d32f2f' : '#666',
            }}
          >
            {sysField.ui?.groupLabel || 'Blood Pressure'}
          </Box>
          <TextField
            variant="standard"
            type="number"
            value={getDisplayValue(sysField) ?? ''}
            disabled={saving || sysField.ui?.readOnly}
            placeholder={sysDeco.placeholder?.split('–')[0] ? `${sysDeco.placeholder}` : 'Sys'}
            onChange={(e) => handleInputChange(sysField.id, e.target.value, selectedUnit)}
            slotProps={{ input: { disableUnderline: true, sx: { fontSize: '14px' } } }}
            sx={{ width: 74 }}
          />
          <Box component="span" sx={{ color: '#94a3b8', fontWeight: 600 }}>
            /
          </Box>
          <TextField
            variant="standard"
            type="number"
            value={getDisplayValue(diaField) ?? ''}
            disabled={saving || diaField.ui?.readOnly}
            placeholder={diaDeco.placeholder || 'Dia'}
            onChange={(e) => handleInputChange(diaField.id, e.target.value, selectedUnit)}
            slotProps={{ input: { disableUnderline: true, sx: { fontSize: '14px' } } }}
            sx={{ width: 64 }}
          />
          <Box sx={{ flex: 1 }} />
          {statusChipNode(chip)}
          {unitAdornment(sysField, selectedUnit, { alsoApplyTo: [diaField.id] })}
        </Box>
        {(sysDeco.helperText || diaDeco.helperText) && (
          <Box sx={{ mt: '3px', ml: '12px', fontSize: '11px', color: '#d32f2f' }}>
            {sysDeco.helperText || diaDeco.helperText}
          </Box>
        )}
      </Box>
    )
  }

  const renderField = (field, group) => {
    // BP pair: render once from the group header, skip the partner.
    if (field.ui?.groupId === 'bp') {
      const partner = group.fields.find((f) => f.ui?.groupId === 'bp' && f.id !== field.id)
      if (!field.ui?.groupHeader) return null
      if (partner) return renderBpPair(field, partner)
    }
    if (field.ui?.input === 'textarea') return renderTextareaField(field)
    if (field.type === 'datetime') return renderDatetimeField(field)
    return renderNumberField(field)
  }

  return (
    <Drawer
      anchor="right"
      open
      onClose={closeFunction}
      PaperProps={{
        sx: {
          width: { xs: '100vw', sm: 620, md: 700 },
          maxWidth: '100vw',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 2.5,
          py: 1.5,
          borderBottom: '1px solid rgba(100, 116, 139, 0.16)',
          background: 'linear-gradient(135deg, #ffffff 0%, #f5f6ff 100%)',
        }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(67, 56, 202, 0.1)',
          }}
        >
          <MonitorHeartOutlinedIcon sx={{ color: '#4338ca', fontSize: 21 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ fontWeight: 800, fontSize: '16px', color: '#1e293b', lineHeight: 1.2 }}>
            Record Vitals
          </Box>
          <Box sx={{ fontSize: '11.5px', color: '#64748b' }}>
            Values outside the usual range are flagged automatically
          </Box>
        </Box>
        <IconButton aria-label="Close" onClick={closeFunction} size="small">
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      {/* Grouped fields */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 2.5, py: 2 }}>
        {vitalsFields.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={26} />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))' },
              gap: '14px 12px',
            }}
          >
            {visibleSections.map((group) => {
              const open = isSectionOpen(group)
              const filled = group.fields.filter(fieldHasValue).length
              return [
                <SectionHeader
                  key={`${group.id}-header`}
                  title={group.title}
                  count={group.fields.length}
                  filled={filled}
                  collapsible={group.collapsible}
                  open={open}
                  onToggle={() =>
                    setOpenSections((prev) => ({ ...prev, [group.id]: !isSectionOpen(group) }))
                  }
                />,
                ...(open ? group.fields.map((field) => renderField(field, group)) : []),
              ]
            })}
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2.5,
          py: 1.5,
          borderTop: '1px solid rgba(100, 116, 139, 0.16)',
          backgroundColor: '#fafafa',
        }}
      >
        <Box sx={{ flex: 1, fontSize: '12px', color: invalidFields.length ? '#b91c1c' : '#64748b' }}>
          {invalidFields.length > 0
            ? `${invalidFields.length} value${invalidFields.length > 1 ? 's look' : ' looks'} implausible — please re-check`
            : recordedCount > 0
              ? `${recordedCount} vital${recordedCount > 1 ? 's' : ''} recorded`
              : 'No vitals entered yet'}
        </Box>
        <Button variant="outlined" onClick={closeFunction} disabled={saving} size="small">
          Cancel
        </Button>
        <Tooltip title={invalidFields.length ? 'Fix the flagged values first' : ''} arrow>
          <span>
            <Button
              variant="contained"
              onClick={saveVital}
              disabled={saving || invalidFields.length > 0}
              size="small"
              startIcon={saving ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <DoneIcon />}
            >
              {saving ? 'Saving…' : 'Save Vitals'}
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Drawer>
  )
}
